import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { MissionMetrics } from "@/types/missions"

/**
 * OKS-31 — Per-mission metrics row.
 *
 * 4 stat tiles: Assigned · Accepted · Posted · Completed. Tiles 2-4
 * carry a derived percentage subtext (`accept_rate`, `post_rate`,
 * `completion_rate`); each percent is `null` when its denominator is 0
 * — the FE renders `—` (NOT `NaN`, NOT `0%`) per PRD §5 lock.
 *
 * Layout:
 *   - ≥md: 4-up inline grid.
 *   - <md: 2-up wrap (Assigned / Accepted on row 1; Posted / Completed
 *     on row 2) per UX Spec §5 mobile.
 */
interface MissionDashboardMetricsProps {
  metrics: MissionMetrics
}

export function MissionDashboardMetrics({
  metrics,
}: MissionDashboardMetricsProps) {
  const { t } = useTranslation()

  return (
    <div
      className="grid grid-cols-2 gap-3 md:grid-cols-4"
      data-testid="mission-dashboard-metrics"
    >
      <MetricTile
        label={t("missions.dashboard.metrics.assigned")}
        count={metrics.assigned}
        rate={null}
        rateLabel={null}
        testId="mission-dashboard-metric-assigned"
      />
      <MetricTile
        label={t("missions.dashboard.metrics.accepted")}
        count={metrics.accepted}
        rate={metrics.accept_rate}
        rateLabel={t("missions.dashboard.metrics.accept_rate_suffix")}
        testId="mission-dashboard-metric-accepted"
      />
      <MetricTile
        label={t("missions.dashboard.metrics.posted")}
        count={metrics.posted}
        rate={metrics.post_rate}
        rateLabel={t("missions.dashboard.metrics.post_rate_suffix")}
        testId="mission-dashboard-metric-posted"
      />
      <MetricTile
        label={t("missions.dashboard.metrics.completed")}
        count={metrics.completed}
        rate={metrics.completion_rate}
        rateLabel={t("missions.dashboard.metrics.completion_rate_suffix")}
        testId="mission-dashboard-metric-completed"
      />
    </div>
  )
}

interface MetricTileProps {
  label: string
  count: number
  rate: number | null
  rateLabel: string | null
  testId: string
  className?: string
}

function MetricTile({
  label,
  count,
  rate,
  rateLabel,
  testId,
  className,
}: MetricTileProps) {
  return (
    <Card className={cn("py-3", className)} data-testid={testId}>
      <CardContent className="flex flex-col gap-1 px-4 py-1">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="text-foreground text-2xl font-semibold tabular-nums">
          {count}
        </span>
        {rateLabel ? (
          <span
            className="text-muted-foreground text-xs"
            data-testid={`${testId}-rate`}
          >
            {rate === null
              ? `${rateLabel}: —`
              : `${rateLabel}: ${Math.round(rate * 100)}%`}
          </span>
        ) : null}
      </CardContent>
    </Card>
  )
}
